import { Progress } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { BiCheck } from 'react-icons/bi';
import { MdEdit } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from '../../../../../../assets/images/white_logo_onboarding.svg';
import { HEADERMENU_PATH } from '../../../../../../routes/RoutesURL';
import { RootState } from '../../../../../../store/rootReducer';
import { ONBOARDING_PATH } from '../../../Onbording';
import { StepperStep, Steppers, onboardingCompletedPercentage } from '../../../utils';
import AccountDropdown from '../../common/AccountDropdown';
import OnboardingBack from '../../common/Back';

type Props = {
    stepperData: Steppers[];
    preferencesIndex: number;
    setPreferencesIndex: (f: number) => void;
};

const SidebarView: React.FC<Props> = ({ stepperData, preferencesIndex, setPreferencesIndex }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const user = useSelector((state: RootState) => state.user.user);
    const investor = useSelector((state: RootState) => state.user.investor);

    const [currentPath, setCurrentPath] = useState('');
    const [isVertical, setIsVertical] = useState(false);
    const [restart, setRestart] = useState(true);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [activeStepdot, setActiveStepDot] = useState<number>(0);

    /**Get current Path */
    useEffect(() => {
        setCurrentPath(location.pathname);
    }, [location]);

    useEffect(() => {
        switch (currentPath) {
            case `${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.BASIC_PROFILE}`:
                setCurrentStep(1);
                setActiveStepDot(1);
                return;
            case `${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.ACCREDITATION}`:
                setCurrentStep(1);
                setActiveStepDot(2);
                return;
            case `${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.INVESTOR_PROFILE}`:
                setCurrentStep(1);
                setActiveStepDot(3);
                return;
            case `${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.NDA}`:
                setCurrentStep(2);
                setActiveStepDot(0);
                return;
            case `${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.INVESTMENT_PREFERENCES}`:
                if (preferencesIndex !== 6) {
                    setCurrentStep(3);
                    setActiveStepDot(preferencesIndex || 1);
                } else {
                    setCurrentStep(-1);
                }
                return;
            case `${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.ACCREDITED_INVESTOR}`:
                setCurrentStep(4);
                setActiveStepDot(0);
                return;
            case `${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.KYC}`:
            case `${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.SIGNPASS}`:
                setCurrentStep(5);
                setActiveStepDot(0);
                return;
            default:
                setCurrentStep(0);
                setActiveStepDot(0);
        }
    }, [currentPath, preferencesIndex]);

    useEffect(() => {
        if (window.innerWidth < 768) {
            setIsVertical(true);
        }
        if (restart) {
            setRestart(false);
        }

        function handleResize() {
            setIsVertical(window.innerWidth < 768 ? true : false);
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [restart]);

    const StepperLabelAndDot = useMemo(() => {
        const redirectToStep = (url?: ONBOARDING_PATH) => {
            if (url === ONBOARDING_PATH.KYC) {
                if (
                    (!investor?.kyc || (investor?.kyc && Object.keys(investor?.kyc).length === 0)) &&
                    investor?.investorType === 'Individual Accredited'
                ) {
                    navigate(url, { state: { selectedType: 'Option' } });
                } else {
                    navigate(url, { state: { selectedType: 'Manual' } });
                }
                return;
            }
            if (url) {
                navigate(url);
            }
        };

        const getColorsOfEditAndbg = (s: Steppers | StepperStep, type: 'text' | 'border' | 'dot', s1?: Steppers) => {
            if (s.step === currentStep && (type === 'text' || type === 'border')) {
                return type === 'text' ? '#FFFFFF' : '#FFFFFF';
            } else if (s1?.step === currentStep && s.step === activeStepdot && type === 'dot') {
                return '#FFFFFF';
            } else if (s.isCompleted) {
                return type === 'text' ? '#FFFFFF' : '#41D17E';
            } else {
                return type === 'dot' ? '#B1C1D4' : type === 'text' ? '#B1C1D4' : '#ACACAC';
            }
        };
        let list = stepperData;
        if (isVertical) {
            list = stepperData.filter((s) => s.step === currentStep);
        }

        const isClick = (list?: Steppers[], index?: number) => {
            index = index || 0;
            if (list && index > 0 && list[index - 1].isCompleted) {
                return true;
            }
            if (list && list[index].isCompleted) {
                return true;
            }
            if (
                list &&
                list[index].url === ONBOARDING_PATH.ACCREDITED_INVESTOR &&
                currentPath === ONBOARDING_PATH.INVESTMENT_PREFERENCES
            ) {
                return true;
            }
            return false;
        };

        const isClickStep = (steps: StepperStep[], stepperIndex: number, index: number, list?: Steppers[]) => {
            if (stepperIndex === 0) {
                if (list && index > 0 && list[index - 1].isCompleted) {
                    return true;
                }
                if (steps[stepperIndex].isCompleted) {
                    return true;
                }
            }
            if (stepperIndex > 0) {
                if (steps[stepperIndex].isCompleted || steps[stepperIndex - 1].isCompleted) {
                    return true;
                }
                if (currentPath === ONBOARDING_PATH.INVESTMENT_PREFERENCES) {
                    if (index > 0 && list && list[index - 1].isCompleted) {
                        return true;
                    }
                }
            }

            return false;
        };

        const onClickCircleOrLabel = (s: Steppers, index: number) => {
            if (
                s.url &&
                s.url === ONBOARDING_PATH.INVESTMENT_PREFERENCES &&
                currentPath !== ONBOARDING_PATH.INVESTMENT_PREFERENCES
            ) {
                setPreferencesIndex(1);
            }
            if (
                currentPath === ONBOARDING_PATH.INVESTMENT_PREFERENCES &&
                s.url === ONBOARDING_PATH.ACCREDITED_INVESTOR
            ) {
                return redirectToStep(s.url);
            }
            if (index > 0 && list[index - 1].isCompleted) {
                redirectToStep(s.url);
            } else {
                if (s.isCompleted) redirectToStep(s.url);
            }
        };

        return (
            <>
                {list.map((s, index) => (
                    <div key={`${index}_${s.label}`} className="flex justify-between pb-8">
                        <div className="flex">
                            {!isVertical && (
                                <div
                                    className={`w-6 h-6 rounded-full border-gray-400 border-2 flex justify-center items-center relative ${
                                        isClick(list, index) ? 'cursor-pointer' : ''
                                    }`}
                                    onClick={() => onClickCircleOrLabel(s, index)}
                                    style={{
                                        borderColor: getColorsOfEditAndbg(s, 'border'),
                                        top: s.steps.length > 0 ? 3 : 0,
                                    }}
                                >
                                    {s.step === currentStep ? (
                                        <MdEdit color="#FFFFFF" />
                                    ) : s.isCompleted ? (
                                        <BiCheck color="#58E866" />
                                    ) : null}
                                </div>
                            )}
                            <div className={`${isVertical ? 'pt-4 pl-2' : 'pl-4'}`}>
                                <p
                                    className={`text-sm font-[WorkSans-Semibold] font-bold ${
                                        isClick(list, index) ? 'cursor-pointer' : ''
                                    }`}
                                    style={{ color: getColorsOfEditAndbg(s, 'text') }}
                                    onClick={() => onClickCircleOrLabel(s, index)}
                                >
                                    {s.label}
                                </p>
                                <div className="flex pt-1">
                                    {s.steps.map((d, index1) => (
                                        <div
                                            key={`${index}_${index1}_${s.label}`}
                                            className={`w-2 h-2 rounded-full mr-2 bg-gray-200 ${
                                                isClickStep(s.steps, index1, index, list) ? 'cursor-pointer' : ''
                                            }`}
                                            style={{ backgroundColor: getColorsOfEditAndbg(d, 'dot', s) }}
                                            onClick={() => {
                                                if (
                                                    s.url &&
                                                    s.url === ONBOARDING_PATH.INVESTMENT_PREFERENCES &&
                                                    index > 0 &&
                                                    list &&
                                                    list[index - 1].isCompleted
                                                ) {
                                                    setActiveStepDot(d.step);
                                                    setPreferencesIndex(d.step);
                                                    navigate(s.url);
                                                } else if (d.isCompleted || d.isActive) redirectToStep(d.url);
                                            }}
                                        ></div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div
                            className="w-10 h-6 hidden md:flex justify-center items-center rounded-xl font-bold font-[WorkSans-Semibold]"
                            style={
                                s.step === currentStep
                                    ? { backgroundColor: '#FFFFFF', color: '#20222B' }
                                    : s.isCompleted
                                    ? { backgroundColor: '#41D17E', color: '#FFFFFF' }
                                    : { backgroundColor: '#142C53', color: '#B1C1D4' }
                            }
                        >
                            {s.steps.length > 0 ? s.steps.length : 1}
                        </div>
                    </div>
                ))}
            </>
        );
    }, [stepperData, isVertical, currentStep, activeStepdot, setPreferencesIndex, currentPath, investor, navigate]);

    return (
        <>
            <div className="hidden md:block bg-[#0F2240] h-full min-h-screen p-4">
                <div className="flex justify-center items-center py-4 pt-0 flex-col">
                    <img src={Logo} alt="companyLogo" className="w-2/3" />
                    <Progress
                        type="circle"
                        percent={onboardingCompletedPercentage(user, investor)}
                        className="py-8"
                        strokeColor={'#41D17E'}
                        strokeWidth={5}
                        trailColor="#B1C1D4"
                        format={(percent) => (
                            <p className="text-[#41D17E] font-[WorkSans] text-4xl" style={{ fontFamily: 'WorkSans' }}>
                                {percent}
                                <span className="text-lg">%</span>
                            </p>
                        )}
                    />
                </div>
                <div>{StepperLabelAndDot}</div>
            </div>

            <div className="block md:hidden bg-[#0F2240] h-full p-4">
                <div className="flex justify-between items-center py-4 pt-0">
                    <img src={Logo} alt="companyLogo" className="w-auto" />
                    <AccountDropdown isMobile />
                </div>
                <div>
                    <div>
                        <OnboardingBack
                            preferencesIndex={preferencesIndex}
                            setPreferencesIndex={setPreferencesIndex}
                            isMobile
                        />
                        <div className="flex justify-between">
                            {StepperLabelAndDot}
                            <Progress
                                type="circle"
                                percent={onboardingCompletedPercentage(user, investor)}
                                strokeColor={'#1B3E8C'}
                                strokeWidth={2}
                                width={100}
                                format={(percent) => (
                                    <p
                                        className="text-[#41D17E] font-[WorkSans] text-3xl"
                                        style={{ fontFamily: 'WorkSans' }}
                                    >
                                        {percent}
                                        <span className="text-base">%</span>
                                    </p>
                                )}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SidebarView;
